import * as React from "react";

import {
  DEFAULT_TIMESTAMP_FORMAT,
  TimestampFormat,
  isTimestampFormat,
} from "./timestampFormat";

interface TimestampFormatContextValue {
  format: TimestampFormat;
  setFormat: (format: TimestampFormat) => void;
}

const TimestampFormatContext = React.createContext<
  TimestampFormatContextValue | undefined
>(undefined);

interface TimestampFormatProviderProps {
  children: React.ReactNode;
  /**
   * localStorage key used to persist the selected format. When unset, the
   * provider is ephemeral (state held in memory only). Callers mounting the
   * provider in distinct parts of the app should supply distinct keys so the
   * preferences don't collide.
   */
  storageKey?: string;
  /** Format to use when no persisted value is found. */
  defaultFormat?: TimestampFormat;
}

export const TimestampFormatProvider: React.FC<
  TimestampFormatProviderProps
> = ({ children, storageKey, defaultFormat = DEFAULT_TIMESTAMP_FORMAT }) => {
  const [format, setFormatState] = React.useState<TimestampFormat>(() => {
    if (!storageKey) {
      return defaultFormat;
    }
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored && isTimestampFormat(stored)) {
        return stored;
      }
    } catch {
      // Storage unavailable (Safari private mode, disabled cookies, etc.)
    }
    return defaultFormat;
  });

  const setFormat = React.useCallback(
    (next: TimestampFormat) => {
      // Mirror the read-side guard: refuse to persist anything that doesn't
      // round-trip through `isTimestampFormat`. Protects against callers that
      // bypass the type system (dynamic JSON, relaxed casts) — without this,
      // a bad value would be persisted and silently overwritten by the
      // read-side default on the next load.
      if (!isTimestampFormat(next)) {
        return;
      }
      setFormatState(next);
      if (!storageKey) {
        return;
      }
      try {
        localStorage.setItem(storageKey, next);
      } catch {
        // Ignore quota / disabled-storage errors
      }
    },
    [storageKey],
  );

  // Cross-tab sync: when another tab writes the same key, mirror the change
  // here so the user's preference stays consistent across windows. The
  // 'storage' event only fires in *other* tabs, so there's no echo loop.
  React.useEffect(() => {
    if (!storageKey) {
      return;
    }
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey || event.storageArea !== localStorage) {
        return;
      }
      if (event.newValue === null) {
        setFormatState(defaultFormat);
        return;
      }
      if (isTimestampFormat(event.newValue)) {
        setFormatState(event.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("storage", onStorage);
    };
  }, [storageKey, defaultFormat]);

  const value = React.useMemo(
    () => ({ format, setFormat }),
    [format, setFormat],
  );

  return (
    <TimestampFormatContext.Provider value={value}>
      {children}
    </TimestampFormatContext.Provider>
  );
};

/**
 * Returns the active format and setter. Returns `undefined` when called
 * outside a {@link TimestampFormatProvider}; consumers should treat that as
 * "use the default format" rather than throwing, because Timestamp is
 * rendered in contexts outside the AgentThread sidebar as well.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useTimestampFormat(): TimestampFormatContextValue | undefined {
  return React.useContext(TimestampFormatContext);
}
